import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Grid, Typography, Button, TextField, InputAdornment, Paper, Divider,
  IconButton, Dialog, DialogContent, DialogTitle, DialogActions, alpha,
  MenuItem, Select, FormControl, Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import ImageIcon from "@mui/icons-material/Image";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import {
  NAVY, NAVY_DARK, GOLD, GOLD_SOFT, PAGE_BG, BORDER,
  TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";
import {
  useGetMediaQuery,
  useDeleteMediaMutation,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { IMedia } from "../../../Api/Interface/api.interface";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNotifier } from "../../../Core/Notifier";
import { useUploads } from "../../../Components/Uploads/UploadContext";

const formatBytes = (b: number): string => {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "numeric",
  });
};

const MediaLibrary: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { showMessage, showErrorMessage } = useNotifier();

  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debounced = useDebounce(searchQuery, 300);
  const [typeFilter, setTypeFilter] = useState<"" | "image" | "video">("");
  const [companyFilter, setCompanyFilter] = useState<number | "">("");
  const [showFilters, setShowFilters] = useState(false);

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const { data, isLoading, isError, refetch } = useGetMediaQuery({
    searchText: currentSearchQuery,
    type: typeFilter || undefined,
  });
  const [deleteMedia] = useDeleteMediaMutation();

  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray(
    (companiesData as any)?.data
  )
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>)
    : [];
  const { enqueue, hasActive } = useUploads();

  const [openDelete, setOpenDelete] = useState(false);
  const [toDelete, setToDelete] = useState<IMedia | null>(null);
  const [preview, setPreview] = useState<IMedia | null>(null);

  // Super-admin upload flow: must pick a tenant before file chooser opens.
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);
  const [pickedCompanyId, setPickedCompanyId] = useState<number | "">("");

  useEffect(() => {
    if (debounced.length >= 2 || debounced === "") setCurrentSearchQuery(debounced);
  }, [debounced]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // Super admin must have picked a company before files were chosen.
    if (isSuperAdmin && !pickedCompanyId) {
      showErrorMessage("Pick a company before uploading");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const companyId = isSuperAdmin ? Number(pickedCompanyId) : undefined;
    const results = await Promise.allSettled(
      Array.from(files).map(f => enqueue(f, companyId))
    );
    const ok = results.filter(r => r.status === "fulfilled").length;
    const failed = results.filter(r => r.status === "rejected");
    if (ok > 0) {
      showMessage(`Uploaded ${ok} file${ok === 1 ? "" : "s"}`);
      refetch();
    }
    failed.forEach((r: any) => {
      const msg = r.reason?.message || "Upload failed";
      if (msg && !msg.toLowerCase().includes("cancel")) showErrorMessage(msg);
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Centralised "click Upload" — super admin first picks a company, then
  // the file dialog opens. Tenant users skip straight to the file dialog.
  const handleUploadClick = () => {
    if (isSuperAdmin) {
      setCompanyPickerOpen(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await deleteMedia(toDelete.id).unwrap();
      if (res.status) {
        showMessage("Media deleted");
        setOpenDelete(false); setToDelete(null);
        refetch();
      }
    } catch {
      showErrorMessage("Failed to delete");
    }
  };

  const items: IMedia[] = useMemo(() => {
    const all = Array.isArray(data?.data) ? ((data?.data as unknown) as IMedia[]) : [];
    return companyFilter ? all.filter(m => m.company_id === companyFilter) : all;
  }, [data, companyFilter]);

  const stats = useMemo(() => {
    const total = items.length;
    const images = items.filter(i => i.media_type === "image").length;
    const videos = items.filter(i => i.media_type === "video").length;
    const size = items.reduce((s, i) => s + (i.file_size || 0), 0);
    return { total, images, videos, size };
  }, [items]);

  if (isLoading) return <PageLoader height="20vh" />;

  return (
    <Box sx={{ bgcolor: PAGE_BG, minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ── Top Bar ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: `1px solid ${BORDER}`, px: 3, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
            boxShadow: `0 6px 18px ${alpha(NAVY, 0.35)}`,
            border: `1px solid ${alpha(GOLD, 0.35)}`,
          }}>
            <PermMediaIcon sx={{ fontSize: 22, color: GOLD }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={22} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
              Media Library
            </Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>
              {stats.total} file{stats.total === 1 ? "" : "s"} · {formatBytes(stats.size)} used
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search by filename..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                "& fieldset": { borderColor: BORDER },
                "&:hover fieldset": { borderColor: alpha(GOLD, 0.6) },
                "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: 1.5 },
              },
            }}
          />
          <Button
            variant="outlined" size="small"
            onClick={() => setShowFilters(v => !v)}
            startIcon={<FilterListIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: 1.5, textTransform: "none", fontSize: 12, fontWeight: 500,
              borderColor: showFilters ? GOLD : BORDER,
              color: showFilters ? NAVY : TEXT_PRIMARY,
              bgcolor: showFilters ? alpha(GOLD, 0.08) : "transparent",
              "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.08), color: NAVY },
            }}
          >
            Filters
          </Button>
          <Divider orientation="vertical" flexItem sx={{ borderColor: BORDER }} />
          <input
            ref={fileInputRef} type="file"
            accept=".jpg,.jpeg,.png,.webp,.mp4"
            multiple hidden
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Button
            variant="contained" color="secondary" disableElevation size="small"
            onClick={handleUploadClick}
            startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3,
              color: NAVY_DARK,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
              "&:hover": {
                background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)`,
                boxShadow: `0 6px 20px ${alpha(GOLD, 0.55)}`,
                transform: "translateY(-1px)",
              },
            }}
          >
            {hasActive ? "Uploading…" : "Upload Media"}
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: 3 }}>
        {showFilters && (
          <Paper elevation={0} sx={{ p: 2.5, mb: 2, borderRadius: 2.5, border: `1px solid ${BORDER}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <FilterListIcon sx={{ fontSize: 18, color: GOLD }} />
              <Typography fontSize={13} fontWeight={700} color={NAVY}>Filters</Typography>
            </Box>
            {isSuperAdmin && (
              <Box sx={{ mb: 2, maxWidth: 320 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: TEXT_SECONDARY, mb: 0.75 }}>
                  Company
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={companyFilter === "" ? "" : String(companyFilter)}
                    onChange={(e) =>
                      setCompanyFilter(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    displayEmpty
                    sx={{ fontSize: 13, borderRadius: 1.5 }}
                  >
                    <MenuItem value="">All companies</MenuItem>
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.companyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            <Box sx={{ display: "flex", gap: 1 }}>
              {[
                { v: "", label: "All", count: stats.total, icon: <PermMediaIcon sx={{ fontSize: 14 }} /> },
                { v: "image", label: "Images", count: stats.images, icon: <ImageIcon sx={{ fontSize: 14 }} /> },
                { v: "video", label: "Videos", count: stats.videos, icon: <OndemandVideoIcon sx={{ fontSize: 14 }} /> },
              ].map(opt => (
                <Button key={opt.v}
                  size="small"
                  startIcon={opt.icon}
                  onClick={() => setTypeFilter(opt.v as any)}
                  sx={{
                    textTransform: "none", fontSize: 12, fontWeight: 600,
                    borderRadius: 1.5, px: 1.75, border: "1px solid",
                    borderColor: typeFilter === opt.v ? GOLD : BORDER,
                    bgcolor: typeFilter === opt.v ? alpha(GOLD, 0.08) : "transparent",
                    color: typeFilter === opt.v ? NAVY : TEXT_SECONDARY,
                    "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.08) },
                  }}>
                  {opt.label} · {opt.count}
                </Button>
              ))}
            </Box>
          </Paper>
        )}

        {/* Gallery */}
        {isError ? (
          <Paper elevation={0} sx={{
            p: 6, borderRadius: "12px", border: `1px solid ${BORDER}`, textAlign: "center",
          }}>
            <Typography sx={{ color: "#b91c1c", fontSize: 13 }}>Error fetching media</Typography>
          </Paper>
        ) : items.length === 0 ? (
          <Paper elevation={0} sx={{
            p: 6, borderRadius: "12px", border: `2px dashed ${BORDER}`, bgcolor: "#fff",
            textAlign: "center",
          }}>
            <PermMediaIcon sx={{ fontSize: 56, color: "#cbd5e1", mb: 1.5 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: NAVY, mb: 0.5 }}>
              Your library is empty
            </Typography>
            <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 3 }}>
              Upload images (jpg, png, webp) or videos (mp4) to start building playlists.
            </Typography>
            <Button
              variant="contained" disableElevation
              onClick={handleUploadClick}
              startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 2.5, textTransform: "none", fontSize: 13, fontWeight: 700, px: 3,
                color: NAVY_DARK,
                background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
                boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
                "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
              }}
            >
              Upload Media
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {items.map((m) => (
              <Grid item xs={6} sm={4} md={3} lg={2.4} key={m.id}>
                <Paper elevation={0} sx={{
                  borderRadius: "12px", overflow: "hidden",
                  border: `1px solid ${BORDER}`, bgcolor: "#fff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: alpha(GOLD, 0.6),
                    boxShadow: `0 8px 22px ${alpha(NAVY, 0.1)}`,
                    transform: "translateY(-2px)",
                  },
                }}>
                  <Box sx={{ position: "relative" }}>
                    {m.media_type === "image" ? (
                      <Box component="img" src={m.url} alt=""
                        sx={{
                          width: "100%", height: 140, objectFit: "cover", display: "block",
                          bgcolor: "#f8fafc",
                        }} />
                    ) : (
                      <Box sx={{
                        height: 140,
                        background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color: "#fff",
                      }}>
                        <OndemandVideoIcon sx={{ fontSize: 40 }} />
                      </Box>
                    )}
                    <Box sx={{
                      position: "absolute", top: 8, left: 8,
                      bgcolor: m.media_type === "image" ? "#3b82f6" : NAVY_DARK,
                      color: "#fff", px: 1, py: 0.25, borderRadius: 1,
                      fontSize: 9, fontWeight: 800, textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                    }}>
                      {m.media_type}
                    </Box>
                  </Box>
                  <Box sx={{ p: 1.5 }}>
                    <Typography sx={{
                      fontSize: 12.5, fontWeight: 700, color: TEXT_PRIMARY,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {m.original_name}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
                      <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY }}>
                        {formatBytes(m.file_size)}
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY }}>
                        {formatDate(m.created_at)}
                      </Typography>
                    </Box>
                    {isSuperAdmin && m.company && (
                      <Box sx={{ mt: 0.75 }}>
                        <Chip
                          size="small"
                          label={m.company.name}
                          sx={{
                            height: 18,
                            fontSize: 10,
                            fontWeight: 600,
                            bgcolor: alpha(NAVY, 0.06),
                            color: NAVY,
                            border: `1px solid ${alpha(NAVY, 0.12)}`,
                            "& .MuiChip-label": { px: 1 },
                          }}
                        />
                      </Box>
                    )}
                    <Divider sx={{ my: 1, borderColor: BORDER }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <IconButton size="small"
                        onClick={() => setPreview(m)}
                        sx={{
                          color: GOLD,
                          "&:hover": { bgcolor: alpha(GOLD, 0.08) },
                        }}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small"
                        onClick={() => { setToDelete(m); setOpenDelete(true); }}
                        sx={{
                          color: "#94a3b8",
                          "&:hover": { color: "#dc2626", bgcolor: "#fef2f2" },
                        }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Preview dialog */}
      <Dialog
        open={!!preview} onClose={() => setPreview(null)}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogContent sx={{ p: 0, bgcolor: NAVY_DARK, position: "relative" }}>
          <IconButton onClick={() => setPreview(null)}
            sx={{
              position: "absolute", top: 12, right: 12, color: "#fff",
              bgcolor: "rgba(0,0,0,0.4)", backdropFilter: "blur(10px)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
              zIndex: 1,
            }}>
            <CloseIcon />
          </IconButton>
          {preview?.media_type === "image" ? (
            <Box component="img" src={preview.url}
              sx={{ width: "100%", maxHeight: "80vh", objectFit: "contain", display: "block" }} />
          ) : preview?.media_type === "video" ? (
            <Box component="video" src={preview.url} controls autoPlay
              sx={{ width: "100%", maxHeight: "80vh", display: "block" }} />
          ) : null}
          {preview && (
            <Box sx={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              p: 2, color: "#fff",
              background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)",
            }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                {preview.original_name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>
                {formatBytes(preview.file_size)} · {preview.media_type}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <DeletePopup
        open={openDelete}
        handleCloseDelete={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Delete Media"
        content={toDelete ? `Permanently delete "${toDelete.original_name}"? Playlists using this file will lose this item.` : ""}
      />

      {/* Super-admin: pick the tenant the upload should belong to before
          opening the OS file picker. */}
      <Dialog
        open={companyPickerOpen}
        onClose={() => setCompanyPickerOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: NAVY, pb: 1 }}>
          Upload Media — pick company
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY, mb: 1.5 }}>
            The files you upload will belong to this tenant.
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={pickedCompanyId === "" ? "" : String(pickedCompanyId)}
              onChange={(e) =>
                setPickedCompanyId(e.target.value === "" ? "" : Number(e.target.value))
              }
              displayEmpty
              sx={{ fontSize: 13, borderRadius: 2.5 }}
            >
              <MenuItem value="">— Pick a company —</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>{c.companyName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            size="small" onClick={() => setCompanyPickerOpen(false)}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            size="small" variant="contained" disableElevation
            disabled={!pickedCompanyId}
            onClick={() => {
              setCompanyPickerOpen(false);
              // Defer to next tick so the dialog finishes closing before the
              // native file picker steals focus.
              setTimeout(() => fileInputRef.current?.click(), 0);
            }}
            sx={{
              borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              color: NAVY_DARK,
              "&:hover": { background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)` },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaLibrary;
